<template>
  <main class="main">
    <div class="anim-wrap" :class="{ loading: loading }">
      <div class="container" v-if="testData">
        <div class="row" v-if="testData.personality">
          <div class="col-lg-5">
            <PersonalityUserCard
              v-if="testData.personality"
              :firstName="testData.firstName"
              :lastName="testData.lastName"
              :personality="testData.personality"
            />
          </div>
          <div class="col-lg-7">
            <PersonalitySplitCard :personality="testData.personality" />
          </div>
        </div>
        <div class="row" v-else>
          <div class="col-12">
            <div class="sheet-panel disabled">
              <div class="sheet-header">
                <h3>Personality insights</h3>
              </div>
              <div class="sheet-content text-center">
                <h6 class="p-5">
                  Complete Insights assessment to unlock more information
                </h6>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <PersonalityHighsAndLowsCard
              v-if="testData.personality"
              :personality="testData.personality"
              :testLanguage="testData.testLanguage"
            />
            <div class="sheet-panel disabled" v-else>
              <div class="sheet-header">
                <h3>Highs and lows</h3>
              </div>
              <div class="sheet-content text-center">
                <h6 class="p-5">
                  Complete Insights assessment<br />
                  to unlock more information
                </h6>
              </div>
            </div>
          </div>
        </div>

        <div class="row pb-before" v-if="transformedScores">
          <div class="col-lg-5">
            <div class="sheet-panel riasec-split">
              <div class="sheet-header">
                <h3>Top interests</h3>
                <button
                  class="info-tip"
                  title="This section outlines your top RIASEC interest code"
                  v-tippy="{ theme: 'navy' }"
                >
                  <font-awesome-icon icon="info" />
                </button>
              </div>
              <div class="sheet-content">
                <div class="chart-wrap">
                  <RiaseChart
                    class="chart-sizer"
                    :chartdata="chartData"
                  ></RiaseChart>
                  <div class="interest-code">
                    <span
                      v-for="item in orderedScores"
                      v-bind:key="item.area"
                      :class="item.area"
                    >
                      {{ truncateString(item.area) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-7">
            <div class="sheet-panel riasec-panel">
              <div class="sheet-header">
                <h3>Interest split</h3>
                <button
                  class="info-tip"
                  title="This section outlines your RIASEC split and descriptions"
                  v-tippy="{ theme: 'navy' }"
                >
                  <font-awesome-icon icon="info" />
                </button>
              </div>
              <div class="sheet-content">
                <div
                  v-for="item in transformedScores.scores"
                  v-bind:key="item.area"
                >
                  <h4>
                    {{ item.area }}
                    <span :class="item.area">{{ item.score }}</span>
                  </h4>
                  <p>{{ item.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row" v-else>
          <div class="col-12">
            <div class="sheet-panel disabled">
              <div class="sheet-header">
                <h3>Interests</h3>
              </div>
              <div class="sheet-content text-center">
                <h6 class="p-5">
                  Complete Pathways assessment to unlock more insights
                </h6>
              </div>
            </div>
          </div>
        </div>

        <div class="row pb-before">
          <div class="col-12">
            <div
              class="sheet-panel"
              v-if="this.careerMatches && testData.careers"
            >
              <div class="sheet-header">
                <h3>Top industries and jobs</h3>
                <button
                  class="info-tip"
                  title="Below are your top industries based on your interests"
                  v-tippy="{ theme: 'navy' }"
                >
                  <font-awesome-icon icon="info" />
                </button>
              </div>
              <div class="sheet-content">
                <div class="top-industries">
                  <div
                    v-for="item in testData.careers.topFiveCareers"
                    v-bind:key="item.key"
                    class="industry"
                    :class="{ active: activeIndustry === Object.keys(item)[0] }"
                    @click="activeIndustry = Object.keys(item)[0]"
                  >
                    <img
                      :src="
                        'https://cdn.traitstack.com/static/' +
                          Object.keys(item)[0] +
                          '.svg'
                      "
                      class="svg-image"
                      alt
                    />
                    <h5>
                      {{ pathwaysCategories[Object.keys(item)[0]] }}
                    </h5>
                    <span class="number">{{ Object.values(item)[0] }}</span>
                  </div>
                </div>
                <div
                  class="all-industries"
                  v-if="careerMatches"
                  @click="activeIndustry = null"
                  :class="{ active: activeIndustry === null }"
                >
                  View all <span>{{ careerMatches.length }}</span>
                </div>
              </div>
            </div>
            <div
              v-else-if="this.careerMatches && testData.pathways"
              class="row"
            >
              <div class="col-12 py-3 mb-4">
                <h2>Interest based job matches</h2>
                <p>
                  The following jobs have a good match with your RIASEC interest
                  profile
                </p>
              </div>
            </div>
            <div class="sheet-panel disabled" v-else>
              <div class="sheet-header">
                <h3>Career matches and top industries</h3>
              </div>
              <div class="sheet-content text-center">
                <h6 class="p-5">
                  Complete Pathways assessment to unlock career matches and top
                  industries
                </h6>
              </div>
            </div>
          </div>
        </div>

        <div class="row job-wrap" v-if="this.careerMatches">
          <div
            class="combo-result col-lg-4 col-md-6 col-sm-6"
            v-for="item in careerMatches"
            v-bind:key="item.code"
            :class="{
              active: item.topcat === activeIndustry || activeIndustry === null,
            }"
          >
            <a
              class="panel-job panel-content panel-link"
              target="_blank"
              :href="'https://traitstack.com/jobs/' + item.code + '?ref=plus'"
              :class="{
                ikigai: item.ikigai,
              }"
            >
              <div class="title-holder">
                <h3>{{ item.title }}</h3>
                <p class="subtitle">{{ item.category }}</p>
                <div class="meta-holder">
                  <button
                    v-if="item.ikigai"
                    class="ikigai-icon"
                    title="This job has a personality and interest match"
                    v-tippy="{ theme: 'navy' }"
                  >
                    <font-awesome-icon icon="trophy" />
                  </button>
                  <button
                    v-if="item.tags.bright_outlook"
                    class="bright"
                    title="This job has a bright future"
                    v-tippy="{ theme: 'navy' }"
                  >
                    <font-awesome-icon icon="arrow-up" />
                  </button>
                </div>
              </div>
              <span class="indicator">
                <font-awesome-icon icon="external-link-alt" />
              </span>
            </a>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div
              class="sheet-panel"
              v-if="testData.careers && transformedScores"
            >
              <div class="sheet-content text-center">
                <p>
                  <a
                    href="https://services.onetcenter.org/"
                    title="This site incorporates information from O*NET Web Services. Click to learn more."
                    target="_blank"
                    ><img
                      src="https://www.onetcenter.org/image/link/onet-in-it.svg"
                      style="width: 130px; height: 60px; border: none"
                      alt="O*NET in-it"
                  /></a>
                </p>
                <p>
                  <small
                    >This site incorporates information from
                    <a href="https://services.onetcenter.org/" target="_blank"
                      >O*NET Web Services</a
                    >
                    by the U.S. Department of Labor, Employment and Training
                    Administration (USDOL/ETA). O*NET® is a trademark of
                    USDOL/ETA.</small
                  >
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="container" v-if="loadError">
        <div class="panel panel-flat text-center">
          <h1>Error</h1>
          <p>
            The profile does not exist or you do not have permission to view the
            information.
          </p>
          <button class="btn btn-link" @click="$router.go(-1)">
            Back to previous page
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
// FireBase
import firebase from "firebase/app";
import "firebase/functions";
import RiaseChart from "@/components/RiasecChart.js";
import PersonalityUserCard from "@/components/PersonalityUserCard";
import PersonalitySplitCard from "@/components/PersonalitySplitCard";
import PersonalityHighsAndLowsCard from "@/components/PersonalityHighsAndLowsCard";
import { pathwaysCategories } from "@/assets/pathwaysCategories.js";
import { psiccCode } from "@/assets/psicc.js";
import { transformations } from "@/assets/jobTransformation.js";

export default {
  name: "resultscombination",
  components: {
    RiaseChart,
    PersonalityUserCard,
    PersonalitySplitCard,
    PersonalityHighsAndLowsCard,
  },
  computed: {
    orderedScores() {
      const scores = JSON.parse(JSON.stringify(this.transformedScores.scores));
      scores.sort(function(b, a) {
        return parseFloat(a.score) - parseFloat(b.score);
      });
      return scores.slice(0, 3);
    },
    transformedCode() {
      let cat = this.activeCareer.substring(0, 2) + "-0000";
      return cat;
    },
  },
  data() {
    return {
      chartData: null,
      activeIndustry: null,
      psiccCode: psiccCode,
      uid: null,
      source: null,
      id: null,
      personaSlug: null,
      testData: null,
      loading: false,
      testLanguage: null,
      loadError: false,
      riasecScore: null,
      careerMatches: null,
      careerTotals: null,
      transformedScores: null,
      pathwaysCategories: pathwaysCategories,
      transformedCareerMatches: [],
    };
  },
  mounted() {
    this.source = this.$route.query.src;
    this.uid = this.$route.query.uid;
    this.id = this.$route.query.id;
    this.fetchData();
  },
  methods: {
    fetchData() {
      if (!this.id && !this.uid) {
        return false;
      }

      this.loading = true;
      let fetchData = firebase.functions().httpsCallable("getSessionData");

      fetchData({
        code: this.id,
        uid: this.uid,
        source: this.source,
      })
        .then((result) => {
          this.loading = false;
          this.loadError = false;
          this.testData = result.data;
          this.getCareerData();
          if (this.source === "aaafc") {
            this.testData.testLanguage = "reduced";
          }
        })
        .catch((error) => {
          this.loadError = true;
          this.loading = false;
          console.log(error);
        });
    },
    getCareerData() {
      if (!this.testData.pathways) {
        return false;
      }

      let pathwaysScore = this.testData.pathways.scores;

      if (!this.testData.pathways.riasec) {
        this.axios
          .get(
            "https://services.onetcenter.org/ws/mnm/interestprofiler/results?answers=" +
              pathwaysScore +
              "&client=traitstack"
          )
          .then((response) => {
            this.transformRiasecScores(response.data.result);
          });
      } else {
        this.transformRiasecScores(this.testData.pathways.riasec);
      }

      this.axios
        .get(
          "https://services.onetcenter.org/ws/mnm/interestprofiler/careers?start=1&end=99&answers=" +
            pathwaysScore +
            "&client=traitstack"
        )
        .then((response) => {
          if (this.testData.personality) {
            this.transformedCareerMatches = this.transformCareers(
              this.psiccCode[this.testData.personality.psiccCode].careerMatch
            );
          }
          this.transformCareerMatches(response.data.career);
        });
    },
    slugifyString(text) {
      text = text.toLowerCase();
      text = text.substring(0, 3);
      return text;
    },
    truncateString(text) {
      text = text.substring(0, 1);
      return text;
    },
    transformCareers(matchedJobs) {
      if (matchedJobs.length) {
        const transformedJobs = [];
        matchedJobs.forEach((career) => {
          let obj = transformations.find((o) => o.job === career);
          if (obj) {
            let cat = obj.code.substring(0, 2);
            obj.topcat = cat + "-0000";
            transformedJobs.push(obj);
          }
        });
        return transformedJobs;
      }
    },
    transformCareerMatches(careerData) {
      const careers = [];

      careerData.forEach((element) => {
        element.data = null;
        let cat = element.code.substring(0, 2);
        element.topcat = cat + "-0000";
        element.category = this.pathwaysCategories[cat + "-0000"];
        let matched = false;
        if (this.transformedCareerMatches.length) {
          this.transformedCareerMatches.forEach((match) => {
            if (match.code == element.code) {
              matched = true;
            }
          });
          if (matched) {
            element.ikigai = true;
          } else {
            element.ikigai = false;
          }
        }
        careers.push(element);
      });

      // Sort by ikigai
      careers.sort(function(a, b) {
        return a.ikigai - b.ikigai;
      });
      careers.reverse();

      this.careerMatches = careers;

      if (this.testData.careers && this.testData.careers.topFiveCareers) {
        this.activeIndustry = Object.keys(
          this.testData.careers.topFiveCareers[0]
        )[0];
      } else {
        this.activeIndustry = null;
      }
    },
    transformRiasecScores(scoreData) {
      let maxValue = 0;

      scoreData.forEach((element) => {
        if (element.score > maxValue) {
          maxValue = element.score;
        }
      });

      maxValue = maxValue + 3;

      const scores = {
        maxValue: maxValue,
        scores: scoreData,
      };

      this.transformedScores = scores;

      let chartData = {
        datasets: [
          {
            data: [],
            backgroundColor: [],
          },
        ],
        labels: [],
      };

      this.transformedScores.scores.forEach((element) => {
        chartData.datasets[0].data.push(element.score);
        chartData.labels.push(element.area);
        if (element.area === "Realistic") {
          chartData.datasets[0].backgroundColor.push("#6fc1ef");
        } else if (element.area === "Investigative") {
          chartData.datasets[0].backgroundColor.push("#8c54e9");
        } else if (element.area === "Artistic") {
          chartData.datasets[0].backgroundColor.push("#f3ce5d");
        } else if (element.area === "Social") {
          chartData.datasets[0].backgroundColor.push("#76dfa1");
        } else if (element.area === "Enterprising") {
          chartData.datasets[0].backgroundColor.push("#fc78e7");
        } else if (element.area === "Conventional") {
          chartData.datasets[0].backgroundColor.push("#E74A69");
        }
      });

      this.chartData = chartData;
    },
  },
};
</script>
